/**
 * DistributedToolingCard — top JA4 fingerprints by **distinct IP
 * spread** in the active window. A single TLS fingerprint appearing
 * across many IPs is a classic distributed-tool / botnet signature
 * (one client stack, many sources).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useFilters } from "@/hooks/useFilters";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { Card, CardHeader, EmptyState, ErrorState, Skeleton } from "@/components/primitives";
import { MonoValue } from "@/components/primitives/MonoValue";
import { fmtNumber, truncateMiddle } from "@/lib/format";
import type { EntityTypeV1 } from "@/types/v1";

interface Props {
  onEntity?: (type: EntityTypeV1, value: string) => void;
}

export function DistributedToolingCard({ onEntity }: Props) {
  const { filters } = useFilters();
  const { tick } = useAutoRefresh();
  const q = useQuery({
    queryKey: ["top-ja4-by-ips", filters, tick],
    queryFn: ({ signal }) => api.topJa4ByIps(filters, signal),
  });

  const rows = q.data?.data ?? [];

  return (
    <Card>
      <CardHeader
        title="Distributed tooling"
        subtitle="JA4 fingerprints spread across the most IPs — a single client stack reused across many sources."
      />
      {q.isLoading ? (
        <Skeleton className="h-48" />
      ) : q.error ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState title="No JA4 fingerprint spans 2+ IPs in this window." />
      ) : (
        <div className="overflow-hidden rounded border border-border">
          <table className="w-full text-sm">
            <thead className="bg-surface-3 text-[11px] tracking-wide text-fg-subtle">
              <tr>
                <th className="px-3 py-2 text-left font-medium">JA4 fingerprint</th>
                <th className="px-3 py-2 text-right font-medium" title="Distinct IP addresses observed using this JA4">IPs</th>
                <th className="px-3 py-2 text-right font-medium">Events</th>
                <th className="px-3 py-2 text-right font-medium" title="High-risk events (score \u2265 80)">High-risk</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.ja4} className="border-t border-border">
                  <td className="px-3 py-2">
                    <MonoValue
                      value={truncateMiddle(r.ja4, 32)}
                      onClick={onEntity ? () => onEntity("ja4", r.ja4) : undefined}
                    />
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-fg">{fmtNumber(r.distinctIps)}</td>
                  <td className="px-3 py-2 text-right font-mono text-fg-muted">{fmtNumber(r.events, { compact: true })}</td>
                  <td className="px-3 py-2 text-right font-mono text-sev-high">{fmtNumber(r.highRisk, { compact: true })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
